import './styles.css'
// TaskCreationModal.tsx
import React, { useCallback, useState } from 'react'
import { Flex, Skeleton } from '@chakra-ui/core'
import { CloseButton } from 'components/ui'
import { ExecutionSkeleton } from '../../TasksOverview/components/DashboardReview/ExecutionSkeleton'
import { Content } from './Content'
import { Footer } from './Footer'
import { BaseModalV3 } from 'components/ui/BaseModal'
import { Header } from './Header'
import { FormStepProvider, StepType, TaskFormData, useFormStepContext } from '../FormStepContext'
import { clearTaskCategoriesFromStorage } from '../Steps/SubtaskCreationStep/Widgets/CategorySelector'
import { TaskPublishingOverlay } from 'components/CommonComponents/TaskPublishingOverlay'
import { AIGenerationProvider } from 'components/TasksV2/AITaskCreation/AIGenerationProvider'
import { PromptGeneratorProvider } from 'components/TasksV2/AITaskCreation/PromptGeneratorContext'
import { AIStepType } from 'components/TasksV2/AITaskCreation/AIFormStepContext'
import { useVmAiEnhance } from '../hooks/useVmAiEnhance'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { FEATURE_FLAGS } from 'constants/featureFlags'
import { BuilderSessionProvider } from '../contexts/BuilderSessionContext'

interface IProps {
  onClose: () => void
  isOpen: boolean
  initialData?: Partial<TaskFormData>
  isEdit?: boolean
  tid?: string
  isVmTask?: boolean
}

interface IInnerProps {
  onClose: () => void
  isOpen: boolean
}

/** Keep form providers unmounted until their initial task data is ready. */
export function TaskCreationModalSkeleton({ onClose }: { onClose: () => void }) {
  return (
    <BaseModalV3
      floating
      isOpen
      trapFocus
      closeOnEsc
      onClose={onClose}
      contentProps={{ className: 'creation-dual-modal' }}
      header={
        <Flex align="center" justify="space-between" py="2px">
          <Skeleton height="18px" width="220px" />
          <CloseButton onClick={onClose} />
        </Flex>
      }
      body={<ExecutionSkeleton />}
      footer={null}
      footerProps={{ style: { display: 'none' } }}
    />
  )
}

const TaskCreationModalContent: React.FC<IInnerProps> = ({ onClose, isOpen }) => {
  const [titleSlot, setTitleSlot] = useState<HTMLDivElement | null>(null)

  const { publicationComplete, isSubmitting, stepStack, isSubtaskAiGenerating } = useFormStepContext()

  // Remember each step's scroll position so navigating back to a step restores it
  // (instead of always jumping to the bottom).
  const activeStepKey = String(stepStack[stepStack.length - 1] ?? '')
  // The prompt helper is a full-bleed chat: message list fills the body, input fills the footer.
  const isPromptGenerator = activeStepKey === AIStepType.AI_PROMPT_GENERATOR
  const isBuilder = activeStepKey === StepType.SUBTASK_TASK_CREATION
  const isSettings = activeStepKey === StepType.TASK_SETTINGS
  const isDualView = isBuilder || isSettings

  return (
    <BaseModalV3
      floating
      trapFocus
      contentProps={isDualView ? { className: 'creation-dual-modal' } : undefined}
      isOpen={isOpen}
      onClose={isSubmitting || isSubtaskAiGenerating ? () => {} : onClose}
      scrollMemoryKey={activeStepKey}
      header={<Header onClose={onClose} builder={isBuilder} settings={isSettings} titleSlotRef={setTitleSlot} />}
      body={isSubmitting ? <TaskPublishingOverlay complete={publicationComplete} /> : <Content titleSlot={titleSlot} />}
      footer={isSubmitting || isDualView ? null : <Footer />}
      bodyProps={isPromptGenerator && !isDualView ? { style: { padding: 0, background: 'white' } } : undefined}
      footerProps={
        isDualView
          ? { style: { display: 'none' } }
          : isPromptGenerator
          ? { style: { padding: 0, background: 'white' } }
          : undefined
      }
    />
  )
}

/**
 * Supplies the AI generation context for the embedded VM "enhance with AI" sub-flow. It wraps the whole
 * modal on purpose — mounting it per-step would reset `isGenerating` between AI_PROMPT and AI_SUBTASK.
 *
 * Mounted only where the sub-flow is reachable (VM creation): the providers pull in the chat/LLM hooks,
 * and elsewhere the AI steps can never be pushed, so consumers fall back to the empty default contexts.
 */
const AiEnhanceBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isVmTask, isEdit } = useFormStepContext()
  const { writeDraftFromLLM } = useVmAiEnhance()
  const isAiVmEnabled = useFeatureFlag(FEATURE_FLAGS.AI_VM)

  if (!isVmTask || isEdit || !isAiVmEnabled) return <>{children}</>

  return (
    <AIGenerationProvider
      onGenerated={writeDraftFromLLM}
      promptSubmittedEvent="vm_ai_subtask_prompt_submitted"
      generatedEvent="vm_ai_subtask_generated"
    >
      {/* Also above the step switch — the prompt-helper conversation is a step of this modal */}
      <PromptGeneratorProvider>{children}</PromptGeneratorProvider>
    </AIGenerationProvider>
  )
}

const TaskCreationModal: React.FC<IProps> = ({
  onClose,
  isOpen,
  initialData,
  tid,
  isEdit = false,
  isVmTask = false,
}) => {
  const handleClose = useCallback(() => {
    clearTaskCategoriesFromStorage()
    onClose()
  }, [onClose])

  return (
    <FormStepProvider onClose={handleClose} initialData={initialData} isEdit={isEdit} tid={tid} isVmTask={isVmTask}>
      <AiEnhanceBridge>
        <BuilderSessionProvider>
          <TaskCreationModalContent onClose={handleClose} isOpen={isOpen} />
        </BuilderSessionProvider>
      </AiEnhanceBridge>
    </FormStepProvider>
  )
}

export default TaskCreationModal
